import React from 'react';
import PropTypes from 'prop-types';

import {Box, Chip, Typography, screenSizeSmaller} from 'views/components';
import {translate} from 'views/module';
import {PendingRounded, CheckCircle, CircleOutlined, Circle} from 'views/icons';

const FormStepper = (props) => {
  const {forms, currentFormIndex, isSummaryStep} = props;
  const isMobile = screenSizeSmaller('md');

  const formTitles = React.useMemo(() => {
    return (forms || [])
        .map(({title, formKey}) => (title))
        .filter((title) => Boolean(title));
  }, [forms]);

  const isFormComplete = (index) => {
    if (isSummaryStep && index === currentFormIndex) {
      return true;
    }
    return index < currentFormIndex;
  };

  const getIcon = (index) => {
    const isCurrentForm = index === currentFormIndex;
    const isComplete = isFormComplete(index);
    const isCurrent = isCurrentForm && !isSummaryStep;
    if (isCurrent) {
      return <PendingRounded color='inherit' sx={{fontSize: '20px', color: '#DA7955'}} />;
    }
    if (isComplete) {
      return <CheckCircle color='inherit' sx={{fontSize: '20px', color: '#188629'}} />;
    }
    return <CircleOutlined color='inherit' sx={{fontSize: '20px', color: '#C5BBB9'}} />;
  };

  const MAX_VISIBLE = isMobile ? 3 : 5;
  const BASE_VISIBLE = MAX_VISIBLE - 1;
  const hasOverflow = formTitles.length > MAX_VISIBLE;

  const visibleItems = React.useMemo(() => {
    if (!hasOverflow) {
      return formTitles.map((title, index) => ({type: 'form', title, index}));
    }

    if (currentFormIndex < BASE_VISIBLE) {
      const firstForms = formTitles.slice(0, BASE_VISIBLE)
          .map((title, index) => ({type: 'form', title, index}));
      const remainingCount = formTitles.length - BASE_VISIBLE;
      return [
        ...firstForms,
        {type: 'more', count: remainingCount},
      ];
    }

    const remainingForms = formTitles.slice(currentFormIndex)
        .map((title, offset) => ({type: 'form', title, index: currentFormIndex + offset}));

    const remainingSlots = MAX_VISIBLE - 1;
    let visibleForms = remainingForms;
    let overflow = 0;

    if (remainingForms.length > remainingSlots) {
      const formSlots = Math.max(remainingSlots - 1, 0); // keep room for a right-side overflow chip
      visibleForms = remainingForms.slice(0, formSlots);
      overflow = remainingForms.length - visibleForms.length;
    }

    const items = [
      {type: 'completed', count: currentFormIndex},
      ...visibleForms,
    ];

    if (overflow > 0) {
      items.push({type: 'more', count: overflow});
    }

    return items;
  }, [formTitles, hasOverflow, currentFormIndex, MAX_VISIBLE, BASE_VISIBLE]);

  const isItemComplete = (item) => {
    if (item.type === 'form') {
      return isFormComplete(item.index);
    } else if (item.type === 'completed') {
      return true;
    }
    return false;
  };

  const renderChip = (item) => {
    if (item.type === 'form') {
      return (
        <Chip
          label={
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: {xs: '12px', sm: '16px'},
                lineHeight: '150%',
                whiteSpace: 'nowrap',
              }}
            >
              {item.title}
            </Typography>
          }
          icon={getIcon(item.index)}
          sx={{
            'backgroundColor': item.index === currentFormIndex ? 'white' : 'transparent',
            'maxWidth': {xs: '120px', sm: 'none'},
            'minWidth': 0,
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
      );
    }

    if (item.type === 'completed') {
      return (
        <Chip
          label={
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: {xs: '12px', sm: '16px'},
                lineHeight: '150%',
                whiteSpace: 'nowrap',
              }}
            >
              {translate('form.FormStepper.More', {count: item.count})}
            </Typography>
          }
          icon={<CheckCircle color='inherit' sx={{color: '#188629', fontSize: '20px'}} />}
          variant="outlined"
          sx={{
            'borderColor': '#188629',
            'maxWidth': {xs: '120px', sm: 'none'},
            'minWidth': 0,
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
      );
    }

    return (
      <Chip
        label={
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: {xs: '12px', sm: '16px'},
              lineHeight: '150%',
              whiteSpace: 'nowrap',
            }}
          >
            {translate('form.FormStepper.More', {count: item.count})}
          </Typography>
        }
        variant="outlined"
        sx={{
          'backgroundColor': 'transparent',
          'borderStyle': 'dashed',
          'borderColor': '#C5BBB9',
          'maxWidth': {xs: '120px', sm: 'none'},
          'minWidth': 0,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Box sx={{display: 'flex', justifyContent: {xs: 'flex-start', sm: 'center'}, minWidth: 0, gap: {xs: 1, sm: 2}}}>
        {
          visibleItems.map((item, index) => {
            const connectorColor = index > 0 && isItemComplete(visibleItems[index - 1]) ? '#188629' : '#C5BBB9';
            return (
              <React.Fragment key={`${item.type}-${item.title || item.count}-${index}`}>
                {index > 0 && (
                  <Box sx={{display: {xs: 'none', sm: 'flex'}, alignItems: 'center', gap: {xs: '3px', sm: '4px'}}}>
                    <Box sx={{height: '1px', width: {xs: '8px', sm: '12px'}, borderRadius: 1, backgroundColor: connectorColor}} />
                    <Circle sx={{fontSize: '4px', color: connectorColor}} />
                    <Box sx={{height: '1px', width: {xs: '8px', sm: '12px'}, borderRadius: 1, backgroundColor: connectorColor}} />
                  </Box>
                )}
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  {renderChip(item)}
                </Box>
              </React.Fragment>
            );
          })
        }
      </Box>
    </Box>
  );
};

FormStepper.propTypes = {
  forms: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    formKey: PropTypes.string,
  })).isRequired,
  currentFormIndex: PropTypes.number.isRequired,
  isSummaryStep: PropTypes.bool,
};

export default React.memo(FormStepper);
